<template>
  <div class="simple-table-container">
    <div class="text-center blue-grey lighten-3 py-2" v-if="selectAll">
      {{ $t("Selected") }}: {{ selectedItemCount() }} {{ $t(tableConfig.selectAllTitleTranslation) }}
    </div>

    <v-simple-table :dense="tableConfig.dense" class="mt-2">
      <thead class="grey lighten-3 table-header">
        <tr>
          <th class="text-left" v-if="tableConfig.allowSelection">
            <v-checkbox
              v-model="selectAll"
              @change="selectAllItemsInPage"
              dense
              class="pa-0"
            ></v-checkbox>
          </th>
          <th
            v-for="(header, index) in tableConfig.headers"
            :key="index"
            class="text-left px-3 py-2"
            @click="onTableHeadClicked(header)"
            :class="{ 'sortable-header': header.allowSorting }"
          >
            <div class="d-flex align-center">
              <span class="text-uppercase font-weight-bold">{{ $t(header.titleTranslation || header.title) }}</span>
              <v-icon
                v-if="header.allowSorting && header.title === paginationSetting.orderBy && !paginationSetting.desc"
                small
                class="ml-1"
              >
                mdi-arrow-down
              </v-icon>
              <v-icon
                v-if="header.allowSorting && header.title === paginationSetting.orderBy && paginationSetting.desc"
                small
                class="ml-1"
              >
                mdi-arrow-up
              </v-icon>
            </div>
          </th>
          <th class="text-left px-3 py-2" v-if="tableData.some(row => row.actions)"></th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="tableData.length === 0">
          <td colspan="9999" class="text-center grey--text font-italic py-4">
            {{ $t('NoData') }}
          </td>
        </tr>

        <tr
          v-for="(row, index) in tableData"
          :key="index"
          :style="tableConfig.allowSelection ? { cursor: 'pointer' } : {}"
          @click="tableConfig.allowSelection ? toggleRowSelect(row) : null"
          class="hoverable-row"
        >
          <td v-if="tableConfig.allowSelection">
            <v-checkbox
              v-model="row.isSelected"
              @change="selectThisItem"
              dense
              class="mt-3"
              @click.stop
            ></v-checkbox>
          </td>

          <td v-for="(field, index) in row.fields" :key="index" class="py-2 px-3">
            {{ showField(field) }}
          </td>

          <td v-if="tableConfig.showActions" :width="tableConfig.actionsWidth" class="py-2 px-3">
            <v-tooltip top v-for="(fieldAction, index) in row.actions" :key="index">
              <template v-slot:activator="{ on }">
                <a
                  v-if="!fieldAction.redirect"
                  v-on="on"
                  class="px-2"
                  @click.prevent="fieldAction.function"
                  @click.stop
                >
                  <v-icon :color="fieldAction.color">{{ fieldAction.icon }}</v-icon>
                </a>
                <v-btn
                  v-else
                  v-on="on"
                  icon
                  :color="fieldAction.color"
                  :link="true"
                  target="_blank"
                  :to="fieldAction.function"
                  @click.stop
                >
                  <v-icon :color="fieldAction.color">{{ fieldAction.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(fieldAction.translation) }}</span>
            </v-tooltip>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <PaginationComponent
      v-if="tableConfig.showPagination"
      :ref="paginationComponentKey"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @on-page-change="loadData($event);"
      :pageSizes="tableConfig.pageSizes || [10, 25, 50, 100, 500]"
      :pageSize="paginationSetting.pageSize"
    />
  </div>
</template>
<script>
import PaginationSetting from '@/ecm/models/PaginationSetting';
import PaginationComponent from '@/components/base/PaginationComponent.vue';
export default {
    name: 'SimpleTable',
    components: {
        PaginationComponent,
    },
    props: {
        tableConfig: {
            type: Object,
            required: true,
        },
        fetchData: {
            type: Function
        },
        tableData: {
            type: Array,
            default: () => []
        }
    },
    async mounted() {
        this.paginationSetting.pageSize = this.tableConfig.defaultPageSize || 50;
        await this.loadData();
    },
    computed: {
        paginationComponentInstance() {
            return this.$refs[this.paginationComponentKey];
        },
    },
    data() {
        return {
            paginationSetting: new PaginationSetting(),
            paginationComponentKey: 'paginationComponentKey',
            selectAll: false,
            totalItems: 0,
            totalPages: 1,
        };
    },
    methods: {
        getCurrentPageSize() {
            return this.paginationSetting.pageSize;
        },
        setPageSize(pageSize) {
            this.paginationSetting.pageSize = pageSize;
            if (this.paginationComponentInstance) {
                this.paginationComponentInstance.changePageSize(pageSize);
            }
            return this.paginationSetting.pageSize;
        },
        async loadData(paginationOptions) {
            if (!this.fetchData) {
                return;
            }

            if (!this.paginationSetting.pageSize) {
                this.paginationSetting.pageSize = this.tableConfig.defaultPageSize || 50;
            }

            if (paginationOptions) {
                if (paginationOptions.page) {
                    this.paginationSetting.page = paginationOptions.page;
                }
                if (paginationOptions.pageSize) {
                    this.paginationSetting.pageSize = paginationOptions.pageSize;
                }
            }

            if (this.paginationSetting.page > this.totalPages && this.totalPages > 0) {
                this.paginationSetting.page = this.totalPages;
            }

            const data = await this.fetchData(this.paginationSetting);

            if (!data) {
                return;
            }

            if (data.totalPages === 0) {
                data.totalPages = 1;
            }

            if (data.result.length === 0 && this.paginationSetting.page > 1) {
                this.paginationComponentInstance.changePage(this.paginationSetting.page - 1);
            }
            this.totalItems = data.totalItems;
            this.totalPages = data.totalPages;
        },
        resetPagination() {
            this.paginationComponentInstance.reset();
        },
        refreshPage() {
            this.paginationComponentInstance.refreshPage();
        },
        showField(field) {
            if (!field) {
                return '';
            }

            if (field.function) {
                return field.function();
            } else return field.title;
        },
        selectAllItemsInPage() {
            this.tableData.forEach(
                (element) => (element.isSelected = this.selectAll)
            );
        },
        selectThisItem() {
            this.selectAll = this.tableData.every((element) => element.isSelected);
        },
        toggleRowSelect(column) {
            if (!this.tableConfig.allowSelection) {
                return;
            }

            column.isSelected = !column.isSelected;
            this.selectAll = this.tableData.every(
                (element) => element.isSelected
            );
        },
        onTableHeadClicked(column) {
            if (!column.allowSorting) {
                return;
            }

            if (this.paginationSetting.orderBy !== column.title || this.paginationSetting.orderBy === '') {
                this.paginationSetting.desc = false;
            } else this.paginationSetting.desc = !this.paginationSetting.desc;

            this.paginationSetting.orderBy = column.title;
            this.loadData();
        },
        selectedItemCount() {
            return this.tableData.filter((x) => x.isSelected).length;
        },
        getSelectedItems() {
            return this.tableData.filter(x => x.isSelected).map(x => x.id);
        }
    },
};
</script>
<style lang="scss">
.simple-table-container {
    td {
        font-weight: 400 !important;
        font-size: 13px !important;
    }
}
</style>